import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  CalendarClock,
  Check,
  ChevronRight,
  CircleDot,
  CreditCard,
  HardDrive,
  Home,
  ImagePlus,
  Library,
  LockKeyhole,
  MonitorPlay,
  Radio,
  Settings,
  ShieldCheck,
  Trash2,
  Users,
  Video,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import CommunicationsSessionActions from "../components/CommunicationsSessionActions";
import CommunicationsDestinationPicker from "../components/CommunicationsDestinationPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatBytes(value) {
  if (value == null) return "Unlimited";
  const bytes = Number(value || 0);
  if (bytes < 1_000_000_000) return `${(bytes / 1_000_000).toFixed(bytes ? 1 : 0)} MB`;
  return `${(bytes / 1_000_000_000).toFixed(1)} GB`;
}

function MultiSelect({ title, items, selected, onToggle }) {
  return (
    <div>
      <div className="text-sm font-semibold text-slate-200">{title}</div>
      <div className="mt-2 max-h-44 overflow-y-auto rounded-lg border border-slate-700 bg-[#090b0f] p-2 space-y-1">
        {items.map((item) => {
          const id = item.id || item.key;
          const active = selected.includes(id);
          return (
            <button
              key={id}
              type="button"
              onClick={() => onToggle(id)}
              className={`w-full px-3 py-2 rounded-md flex items-center gap-3 text-left ${active ? "bg-[#183c73]" : "hover:bg-white/5"}`}
            >
              <span className={`w-4 h-4 rounded border flex items-center justify-center ${active ? "bg-[#8bb6ff] border-[#8bb6ff]" : "border-slate-600"}`}>
                {active && <Check className="w-3 h-3 text-slate-950" />}
              </span>
              <span className="min-w-0">
                <span className="text-sm block truncate">{item.label || item.name}</span>
                {item.username && <span className="text-xs text-slate-500">@{item.username}</span>}
              </span>
            </button>
          );
        })}
        {!items.length && <div className="text-xs text-slate-500 p-3">No options available.</div>}
      </div>
    </div>
  );
}

function CreateSessionModal({
  mode,
  onClose,
  destinations,
  providers,
  accessOptions,
  account,
  cloudRecordingEnabled,
  maxDestinations,
}) {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const serviceOnly = account?.classification === "communications_service";
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [visibility, setVisibility] = useState(mode === "meeting" ? "internal" : "public");
  const [orientation, setOrientation] = useState("landscape");
  const [thumbnailUrl, setThumbnailUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [selectedDestinations, setSelectedDestinations] = useState([]);
  const [accessMode, setAccessMode] = useState(serviceOnly ? "invite_only" : "all_nationals");
  const [allowedFunctions, setAllowedFunctions] = useState([]);
  const [allowedPeople, setAllowedPeople] = useState([]);
  const [allowGuests, setAllowGuests] = useState(serviceOnly || mode !== "recording");
  const [autoRecord, setAutoRecord] = useState(mode === "recording" && cloudRecordingEnabled);
  const [publicChat, setPublicChat] = useState(true);
  const [busy, setBusy] = useState(false);

  const sessionType = mode === "recording" ? "recording" : mode === "meeting" ? "meeting" : "live";
  const heading = mode === "recording" ? "Create recording" : mode === "meeting" ? "Create meeting" : "Create live stream";
  const needsFunctions = ["functions", "functions_or_specific"].includes(accessMode);
  const needsPeople = ["specific", "functions_or_specific"].includes(accessMode);
  const toggle = (setter) => (id) => setter((current) => current.includes(id) ? current.filter((value) => value !== id) : [...current, id]);

  const uploadThumbnail = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const response = await axios.post(`${API}/communications/uploads/thumbnail`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setThumbnailUrl(response.data.url);
      toast.success("Thumbnail uploaded");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Thumbnail upload failed");
    } finally {
      setUploading(false);
    }
  };

  const create = async () => {
    if (!title.trim()) return toast.error("Add a title first");
    if (mode === "live" && !selectedDestinations.length) return toast.error("Choose at least one destination");
    if (mode === "recording" && !cloudRecordingEnabled) return toast.error("Cloud recording requires recording storage access.");

    setBusy(true);
    try {
      const response = await axios.post(`${API}/communications/rooms`, {
        title: title.trim(),
        description: description.trim(),
        session_type: sessionType,
        scheduled_at: scheduledAt ? new Date(scheduledAt).toISOString() : null,
        visibility: mode === "meeting" ? "internal" : visibility,
        orientation,
        initial_layout: "presenter",
        thumbnail_url: thumbnailUrl || null,
        access_mode: serviceOnly ? "invite_only" : accessMode,
        allowed_ministries: serviceOnly ? [] : allowedFunctions,
        allowed_user_ids: serviceOnly ? [] : allowedPeople,
        allow_guests: allowGuests,
        recording_enabled: cloudRecordingEnabled,
        auto_record: cloudRecordingEnabled && autoRecord,
        selected_destination_ids: mode === "live" ? selectedDestinations : [],
        public_chat_enabled: publicChat,
      });
      const room = response.data;
      if (room.website_live_review_status === "pending") {
        toast.success("Session created. Nation Live was submitted for approval.");
      }
      const fragment = new URLSearchParams();
      if (room.guest_invite_code) fragment.set("invite", room.guest_invite_code);
      navigate(`/communications/studio/${room.id}${fragment.toString() ? `#${fragment.toString()}` : ""}`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not create session");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-3 sm:p-5">
      <div className="w-full max-w-3xl max-h-[94vh] rounded-2xl border border-slate-700 bg-[#111318] text-white flex flex-col overflow-hidden shadow-2xl">
        <div className="px-6 py-5 border-b border-slate-800 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[.2em] text-kingdom-gold font-bold">Yasharal Live Studio</div>
            <h2 className="text-2xl font-semibold mt-1">{heading}</h2>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-white/5"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-6">
          <div>
            <div className="text-sm font-semibold mb-3">Source</div>
            <div className="rounded-xl border border-[#8bb6ff] bg-[#183c73]/35 p-4">
              <div className="flex gap-2 items-center font-semibold"><Video className="w-4 h-4" /> In Studio</div>
              <p className="text-xs text-slate-400 mt-1">Camera, microphone, guests, screen share, banners, backgrounds and branding.</p>
            </div>
          </div>

          {mode === "live" && (
            <CommunicationsDestinationPicker
              destinations={destinations}
              providers={providers}
              selectedIds={selectedDestinations}
              maxDestinations={maxDestinations}
              onChange={setSelectedDestinations}
            />
          )}

          <div>
            <label className="text-sm text-slate-300">Title</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-2 bg-[#0b0d11] border-slate-700 text-white" />
          </div>
          <div>
            <label className="text-sm text-slate-300">Description</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} className="mt-2 w-full rounded-md bg-[#0b0d11] border border-slate-700 p-3 text-white" />
          </div>

          <div>
            <label className="text-sm text-slate-300">Thumbnail / cover</label>
            <div className="mt-2 flex flex-wrap items-center gap-4">
              <div className="w-48 aspect-video rounded-xl border border-dashed border-slate-600 bg-[#090b0f] overflow-hidden flex items-center justify-center">
                {thumbnailUrl ? <img src={thumbnailUrl} alt="" className="w-full h-full object-cover" /> : <ImagePlus className="w-8 h-8 text-slate-600" />}
              </div>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => uploadThumbnail(e.target.files?.[0])} />
              <Button type="button" variant="outline" className="border-slate-600 text-white" disabled={uploading} onClick={() => fileRef.current?.click()}>
                {uploading ? "Uploading…" : "Upload thumbnail"}
              </Button>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-slate-300">Orientation</label>
              <select value={orientation} onChange={(e) => setOrientation(e.target.value)} className="mt-2 w-full rounded-md bg-[#0b0d11] border border-slate-700 p-2.5">
                <option value="landscape">Landscape 16:9</option>
                <option value="portrait">Portrait 9:16</option>
                <option value="square">Square 1:1</option>
              </select>
            </div>
            <div className="rounded-xl border border-slate-700 bg-[#0b0d11] p-3 text-sm text-slate-400">
              Program layouts are selected visually inside Studio underneath the presenter preview.
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {mode !== "meeting" && (
              <div>
                <label className="text-sm text-slate-300">Visibility</label>
                <select value={visibility} onChange={(e) => setVisibility(e.target.value)} className="mt-2 w-full rounded-md bg-[#0b0d11] border border-slate-700 p-2.5">
                  <option value="public">Public</option>
                  <option value="unlisted">Unlisted</option>
                  {!serviceOnly && <option value="internal">Nationals only</option>}
                </select>
              </div>
            )}
            <div>
              <label className="text-sm text-slate-300">Schedule</label>
              <Input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} className="mt-2 bg-[#0b0d11] border-slate-700 text-white" />
            </div>
          </div>

          {serviceOnly ? (
            <div className="rounded-xl border border-slate-700 bg-[#0b0d11] p-4">
              <div className="flex gap-2 items-center font-semibold"><LockKeyhole className="w-4 h-4" /> Invite participants</div>
              <p className="text-xs text-slate-400 mt-2">Participants join with your secure link/code. The Nation directory is not exposed.</p>
              <label className="flex gap-3 mt-4 text-sm"><input type="checkbox" checked={allowGuests} onChange={(e) => setAllowGuests(e.target.checked)} /> Allow invited guests</label>
            </div>
          ) : (
            <div className="rounded-xl border border-slate-700 bg-[#0b0d11] p-4 space-y-4">
              <div className="flex gap-2 items-center font-semibold"><LockKeyhole className="w-4 h-4" /> Who can join?</div>
              <select value={accessMode} onChange={(e) => setAccessMode(e.target.value)} className="w-full rounded-md bg-[#090b0f] border border-slate-700 p-2.5">
                <option value="all_nationals">All Nationals</option>
                <option value="functions">Selected functions</option>
                <option value="specific">Selected people</option>
                <option value="functions_or_specific">Selected functions or people</option>
                <option value="invite_only">Invite only</option>
              </select>
              {needsFunctions && <MultiSelect title="Functions" items={accessOptions.functions || []} selected={allowedFunctions} onToggle={toggle(setAllowedFunctions)} />}
              {needsPeople && <MultiSelect title="Specific Nationals" items={accessOptions.people || []} selected={allowedPeople} onToggle={toggle(setAllowedPeople)} />}
              <label className="flex gap-3 text-sm"><input type="checkbox" checked={allowGuests} onChange={(e) => setAllowGuests(e.target.checked)} /> Allow outside guests with secure link/code</label>
            </div>
          )}

          <div className="rounded-xl border border-slate-700 bg-[#0b0d11] p-4">
            {cloudRecordingEnabled ? (
              <label className="flex gap-3 items-center text-sm"><input type="checkbox" checked={autoRecord} onChange={(e) => setAutoRecord(e.target.checked)} /> Save a cloud recording of this session</label>
            ) : (
              <div className="text-sm text-slate-400">Cloud recording storage is available to Shaba, Sam, and paid Studio plans.</div>
            )}
          </div>

          {mode === "live" && selectedDestinations.includes("nation_live") && (
            <label className="flex gap-3 text-sm"><input type="checkbox" checked={publicChat} onChange={(e) => setPublicChat(e.target.checked)} /> Enable chat on Nation Live if approved</label>
          )}
        </div>

        <div className="px-6 py-5 border-t border-slate-800 flex justify-end gap-3">
          <Button variant="outline" className="border-slate-600 text-white" onClick={onClose}>Cancel</Button>
          <Button className="bg-[#8bb6ff] text-slate-950 min-w-40" disabled={busy || uploading} onClick={create}>
            {busy ? "Creating…" : scheduledAt ? "Schedule" : heading}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ConnectedAccounts({ destinations, providers, onRefresh }) {
  const connectedProviders = new Set(destinations.filter((item) => item.id !== "nation_live").map((item) => item.provider));

  const connect = async (provider) => {
    if (!provider.ready) return toast.error(provider.setup_message || `${provider.label} connection is not ready yet.`);
    try {
      const response = await axios.get(`${API}/communications/social/connect/${provider.provider}`);
      if (!response.data?.authorization_url) throw new Error("No authorization page was returned");
      window.location.href = response.data.authorization_url;
    } catch (error) {
      toast.error(error?.response?.data?.detail || error.message || "Could not start connection");
    }
  };

  const remove = async (destination) => {
    try {
      await axios.delete(`${API}/communications/destinations/${destination.id}`);
      toast.success(`${destination.name} disconnected`);
      await onRefresh();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not disconnect destination");
    }
  };

  return (
    <section id="destinations" className="rounded-2xl border border-slate-800 bg-[#111318] p-6 scroll-mt-24">
      <div className="flex items-center gap-3">
        <MonitorPlay className="w-6 h-6 text-[#9dc1ff]" />
        <div>
          <h2 className="text-xl font-semibold">Connected accounts</h2>
          <p className="text-sm text-slate-400 mt-1">This section only manages account connections. Actual broadcast choices happen inside Create Live Stream.</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mt-5">
        {providers.map((provider) => {
          const connected = connectedProviders.has(provider.provider) || provider.connected;
          const canReconnect = provider.provider === "youtube" && connected;
          return (
            <button
              type="button"
              key={provider.provider}
              disabled={connected && !canReconnect}
              onClick={() => connect(provider)}
              className={`rounded-xl border p-4 text-left ${connected ? "border-green-700/40 bg-green-500/5" : "border-slate-700 bg-[#0b0d11] hover:border-[#8bb6ff]"} ${canReconnect ? "cursor-pointer hover:border-[#8bb6ff]" : ""}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-semibold">{provider.label}</span>
                {connected ? <Check className="w-4 h-4 text-green-400" /> : <span className="text-[#9dc1ff] text-lg">+</span>}
              </div>
              <div className="text-xs text-slate-500 mt-2">{canReconnect ? "Reconnect YouTube authorization" : connected ? "Connected" : provider.ready ? provider.action : "Setup pending"}</div>
            </button>
          );
        })}
      </div>

      {destinations.filter((item) => item.id !== "nation_live").length > 0 && (
        <div className="mt-5 pt-5 border-t border-slate-800 space-y-2">
          {destinations.filter((item) => item.id !== "nation_live").map((destination) => (
            <div key={destination.id} className="flex items-center gap-3 rounded-lg border border-slate-800 p-3">
              <div className="flex-1 min-w-0"><div className="font-semibold">{destination.name}</div><div className="text-xs text-slate-500 truncate">{destination.account_label}</div></div>
              <button type="button" onClick={() => remove(destination)} className="p-2 text-slate-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function StorageCard({ storage, onBuy, plans = [] }) {
  const plan = storage?.plan;
  const launchPlan = plans.find((item) => item.key === "launch");
  return (
    <section className="rounded-2xl border border-slate-800 bg-[#111318] p-6">
      <div className="flex items-center gap-3"><HardDrive className="w-6 h-6 text-[#9dc1ff]" /><div><h2 className="text-xl font-semibold">Recording storage</h2><p className="text-sm text-slate-400 mt-1">Private cloud storage for saved Studio recordings.</p></div></div>
      {plan ? (
        <div className="mt-5 grid md:grid-cols-3 gap-4">
          <div className="rounded-xl border border-slate-700 p-4"><div className="text-xs text-slate-500">Plan</div><div className="font-semibold mt-1">{plan.name}</div></div>
          <div className="rounded-xl border border-slate-700 p-4"><div className="text-xs text-slate-500">Storage</div><div className="font-semibold mt-1">{formatBytes(storage.storage_bytes)} used</div><div className="text-xs text-slate-500 mt-1">of {storage.storage_limit_bytes == null ? "Unlimited" : formatBytes(storage.storage_limit_bytes)}</div></div>
          <div className="rounded-xl border border-slate-700 p-4"><div className="text-xs text-slate-500">Recordings</div><div className="font-semibold mt-1">{storage.recording_count} saved</div><div className="text-xs text-slate-500 mt-1">of {storage.recording_count_limit == null ? "Unlimited" : storage.recording_count_limit}</div></div>
        </div>
      ) : (
        <div className="mt-5 rounded-xl border border-slate-700 bg-[#0b0d11] p-4 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1"><div className="font-semibold">{launchPlan?.name || "Yashar’al Live Launch"}</div>{launchPlan&&<div className="mt-1 space-y-0.5 text-xs text-slate-400"><div>Normal monthly: ${launchPlan.monthly_price_usd}/mo</div>{launchPlan.annual_price_usd!=null&&<div>Normal annual: ${Number(launchPlan.annual_price_usd).toFixed(2)}/yr · {launchPlan.annual_discount_percent}% below 12 normal monthly payments</div>}{launchPlan.launch_offer_available&&launchPlan.launch_price_usd!=null&&<div className="text-amber-200">Founding monthly: ${launchPlan.launch_price_usd}/mo for {launchPlan.launch_price_months} billing cycles, then normal monthly.</div>}{launchPlan.launch_offer_available&&launchPlan.launch_annual_price_usd!=null&&<div className="text-amber-200">Founding annual: ${Number(launchPlan.launch_annual_price_usd).toFixed(2)} first year · {launchPlan.launch_annual_discount_percent}% off normal annual.</div>}</div>}<p className="text-xs text-slate-400 mt-2">Custom branding, multistreaming, cloud recording, secure meetings and full Studio production tools.</p></div>
          <Button onClick={() => onBuy("launch")} className="bg-[#8bb6ff] text-slate-950"><CreditCard className="w-4 h-4 mr-2" />Choose Launch</Button>
        </div>
      )}
    </section>
  );
}

function ReviewQueue({ reviews, onDecision }) {
  if (!reviews.length) return null;
  return (
    <section className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-6">
      <div className="flex items-center gap-3"><ShieldCheck className="w-6 h-6 text-amber-300" /><div><h2 className="text-xl font-semibold">Website Live review</h2><p className="text-sm text-slate-400">Pending requests for gov.yasharal.vip/live.</p></div></div>
      <div className="mt-5 space-y-3">
        {reviews.map((review) => (
          <div key={review.id} className="rounded-xl border border-slate-700 bg-[#0b0d11] p-4 flex flex-col sm:flex-row gap-4 sm:items-center">
            <div className="flex-1"><div className="font-semibold">{review.room_title || "Untitled live"}</div><div className="text-xs text-slate-500 mt-1">@{review.requested_by_username || "user"}</div></div>
            <div className="flex gap-2"><Button variant="outline" className="border-red-500/40 text-red-300" onClick={() => onDecision(review.id, "deny")}>Deny</Button><Button className="bg-green-600 text-white" onClick={() => onDecision(review.id, "approve")}>Approve Nation Live</Button></div>
          </div>
        ))}
      </div>
    </section>
  );
}

function Sidebar({ serviceOnly }) {
  return (
    <aside className="w-64 flex-shrink-0 border-r border-slate-800 bg-[#111318] p-5 hidden lg:flex flex-col sticky top-0 h-screen">
      <div className="flex items-center gap-3 mb-8"><div className="h-11 w-11 rounded-xl bg-royal-blue flex items-center justify-center font-bold text-xl">YSQ</div><div><div className="font-semibold">Yasharal Live Studio</div><div className="text-xs text-slate-500">{serviceOnly ? "Streaming service" : "Nation Yasharal Live"}</div></div></div>
      <nav className="space-y-1">
        <Link to="/yasharal-live" className="flex items-center gap-3 rounded-lg bg-royal-blue/15 text-[#9dc1ff] px-3 py-3"><Home className="w-5 h-5" /> Home</Link>
        <Link to="/yasharal-live/recordings" className="flex items-center gap-3 rounded-lg hover:bg-white/5 px-3 py-3"><Library className="w-5 h-5" /> Recordings</Link>
        <a href="#destinations" className="flex items-center gap-3 rounded-lg hover:bg-white/5 px-3 py-3"><MonitorPlay className="w-5 h-5" /> Connected accounts</a>
      </nav>
    </aside>
  );
}

export default function CommunicationsHome() {
  const navigate = useNavigate();
  const location = useLocation();
  const standaloneLive = window.location.hostname === "live.yasharal.vip" || window.location.hostname === "yasharal-live-frontend-production.up.railway.app";
  const [feed, setFeed] = useState({ live: [], upcoming: [], recordings: [] });
  const [destinations, setDestinations] = useState([]);
  const [providers, setProviders] = useState([]);
  const [accessOptions, setAccessOptions] = useState({ people: [], functions: [] });
  const [account, setAccount] = useState(null);
  const [status, setStatus] = useState(null);
  const [storage, setStorage] = useState(null);
  const [livePlans, setLivePlans] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [modal, setModal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionSearch, setSessionSearch] = useState("");
  const [sessionFilter, setSessionFilter] = useState("all");
  const [sessionSort, setSessionSort] = useState("soonest");

  const load = useCallback(async () => {
    try {
      const [feedResponse, destinationResponse, providerResponse, accessResponse, accountResponse, statusResponse, storageResponse, plansResponse] = await Promise.all([
        axios.get(`${API}/communications/feed`),
        axios.get(`${API}/communications/destinations`),
        axios.get(`${API}/communications/social/providers`),
        axios.get(`${API}/communications/access-options`),
        axios.get(`${API}/communications/account`),
        axios.get(`${API}/communications/status`),
        axios.get(`${API}/communications/storage`),
        axios.get(`${API}/communications/billing/plans`),
      ]);
      setFeed(feedResponse.data || { live: [], upcoming: [], recordings: [] });
      setDestinations(destinationResponse.data?.destinations || []);
      setProviders(providerResponse.data?.providers || []);
      setAccessOptions(accessResponse.data || { people: [], functions: [] });
      setAccount(accountResponse.data || null);
      setStatus(statusResponse.data || null);
      setStorage(storageResponse.data || null);
      setLivePlans(plansResponse.data?.plans || []);
      if (accountResponse.data?.can_review_website_live) {
        const reviewResponse = await axios.get(`${API}/communications/website-live/reviews?status=pending`);
        setReviews(reviewResponse.data || []);
      } else {
        setReviews([]);
      }
    } catch (error) {
      if ([401, 403].includes(error?.response?.status)) navigate("/yasharal-live/login");
      else toast.error(error?.response?.data?.detail || "Communications dashboard could not load");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    if (query.get("destination_connected")) {
      toast.success(`${query.get("destination_connected")} connected`);
      window.history.replaceState({}, "", "/yasharal-live");
      load();
    }
    if (query.get("studio_billing") === "success") {
      toast.success("Studio plan payment received. Access will update automatically.");
      window.history.replaceState({}, "", "/yasharal-live");
      load();
    }
    if (query.get("destination_error")) toast.error("Destination connection was cancelled or failed");
  }, [location.search, load]);

  const decideReview = async (id, decision) => {
    try {
      await axios.post(`${API}/communications/website-live/reviews/${id}/${decision}`, { note: "" });
      toast.success(decision === "approve" ? "Nation Live approved" : "Nation Live denied");
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not update review");
    }
  };

  const buyPlan = async (plan) => {
    try {
      const response = await axios.post(`${API}/communications/billing/checkout`, {
        plan,
        origin_url: window.location.origin,
      });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not open Studio checkout");
    }
  };

  const allSessions = [...(feed.live || []), ...(feed.upcoming || [])];
  const sessions = allSessions
    .filter((session) => {
      const q = sessionSearch.trim().toLowerCase();
      const type = String(session.session_type || "meeting").toLowerCase();
      const statusValue = String(session.status || "").toLowerCase();
      if (sessionFilter === "live" && statusValue !== "live") return false;
      if (sessionFilter === "upcoming" && !["ready", "scheduled"].includes(statusValue)) return false;
      if (["meeting", "recording"].includes(sessionFilter) && type !== sessionFilter) return false;
      if (sessionFilter === "stream" && type !== "live") return false;
      if (!q) return true;
      return [session.title, session.description, session.created_by_display_name, session.created_by_username]
        .filter(Boolean).join(" ").toLowerCase().includes(q);
    })
    .sort((a, b) => {
      if (sessionSort === "host") return String(a.created_by_display_name || a.created_by_username || "").localeCompare(String(b.created_by_display_name || b.created_by_username || ""));
      if (sessionSort === "newest") return new Date(b.created_at || b.scheduled_at || 0) - new Date(a.created_at || a.scheduled_at || 0);
      const at = new Date(a.scheduled_at || a.created_at || 0).getTime();
      const bt = new Date(b.scheduled_at || b.created_at || 0).getTime();
      return at - bt;
    });
  const serviceOnly = account?.classification === "communications_service";
  const maxDestinations = storage?.plan?.max_destinations;
  const canManageSession = (session) => Boolean(
    account?.communications_manager
    || String(session.created_by_user_id || "") === String(account?.id || "")
    || (session.presenter_user_ids || []).map(String).includes(String(account?.id || ""))
  );

  return (
    <div className="min-h-screen bg-[#0b0d11] text-white flex">
      <Sidebar serviceOnly={serviceOnly} />
      <div className="flex-1 min-w-0">
        <header className="min-h-16 sm:h-20 border-b border-slate-800 px-4 sm:px-6 lg:px-10 py-3 flex items-center justify-between gap-3 sticky top-0 z-30 bg-[#0b0d11]/95">
          <div className="min-w-0">
            <h1 className="text-base sm:text-xl font-semibold truncate">Yashar’al Live Studio</h1>
            <p className="text-[11px] sm:text-xs text-slate-500">Meet · Record · Produce · Broadcast</p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              type="button"
              onClick={() => navigate(standaloneLive ? "/" : -1)}
              className="hidden sm:inline-flex text-sm text-slate-400 hover:text-white"
            >
              Back
            </button>
            <a href="https://gov.yasharal.vip" className="text-xs sm:text-sm font-semibold text-[#9dc1ff] hover:text-white">
              gov site
            </a>
            {!serviceOnly && <a href="https://gov.yasharal.vip/nationals/portal" className="hidden md:inline text-sm text-slate-400">Nation Portal</a>}
            <Settings className="w-5 h-5 flex-shrink-0" />
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-6 sm:py-10 space-y-8 sm:space-y-12">
          {!status?.configured && (
            <section className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-5">
              <div className="font-semibold text-amber-100">Media server setup is still incomplete</div>
              <p className="text-sm text-amber-100/80 mt-2">You can create and schedule sessions now. Camera, microphone, screen share and actual live/record output require the LiveKit media service connection.</p>
            </section>
          )}

          {account?.can_review_website_live && <ReviewQueue reviews={reviews} onDecision={decideReview} />}

          <section>
            <h2 className="text-3xl font-semibold mb-2">Create</h2>
            <p className="text-sm text-slate-500 mb-6">Choose the actual destinations inside the Live Stream setup for that exact session.</p>
            <div className="grid md:grid-cols-3 gap-5">
              <button onClick={() => setModal("live")} className="text-left rounded-2xl border border-slate-700 bg-[#111318] hover:border-[#8bb6ff] p-6 flex gap-4 items-center">
                <div className="h-14 w-14 rounded-xl bg-[#183c73] flex items-center justify-center"><Video className="w-7 h-7 text-[#9dc1ff]" /></div>
                <div><div className="text-xl font-semibold">Live stream</div><div className="text-sm text-slate-400 mt-1">Choose Nation Live, YouTube, Facebook, TikTok and other available accounts inside setup.</div></div>
              </button>
              <button disabled={!storage?.cloud_recording_enabled} onClick={() => setModal("recording")} className="text-left rounded-2xl border border-slate-700 bg-[#111318] hover:border-[#8bb6ff] disabled:opacity-45 disabled:cursor-not-allowed p-6 flex gap-4 items-center">
                <div className="h-14 w-14 rounded-xl bg-[#183c73] flex items-center justify-center"><CircleDot className="w-7 h-7 text-[#9dc1ff]" /></div>
                <div><div className="text-xl font-semibold">Recording</div><div className="text-sm text-slate-400 mt-1">{storage?.cloud_recording_enabled ? "Record and save to your private library." : "Requires recording storage access."}</div></div>
              </button>
              <button onClick={() => setModal("meeting")} className="text-left rounded-2xl border border-slate-700 bg-[#111318] hover:border-[#8bb6ff] p-6 flex gap-4 items-center">
                <div className="h-14 w-14 rounded-xl bg-[#183c73] flex items-center justify-center"><Users className="w-7 h-7 text-[#9dc1ff]" /></div>
                <div><div className="text-xl font-semibold">{serviceOnly ? "Studio meeting" : "Private meeting"}</div><div className="text-sm text-slate-400 mt-1">Interactive camera, mic, chat and screen sharing.</div></div>
              </button>
            </div>
          </section>

          <StorageCard storage={storage} onBuy={buyPlan} plans={livePlans} />

          <section>
            <div className="space-y-4 mb-5">
              <div className="flex flex-wrap items-center justify-between gap-3"><div><h2 className="text-2xl font-semibold">Sessions</h2><p className="text-xs text-slate-500 mt-1">Find the live, meeting, host, or scheduled session you actually want.</p></div><span className="text-sm text-slate-500">{sessions.length} shown · {allSessions.length} available</span></div>
              <div className="grid lg:grid-cols-[1fr_auto_auto] gap-3">
                <Input value={sessionSearch} onChange={(e)=>setSessionSearch(e.target.value)} placeholder="Search title, description, or host…" className="bg-[#0b0d11] border-slate-700 text-white"/>
                <select value={sessionFilter} onChange={(e)=>setSessionFilter(e.target.value)} className="rounded-md bg-[#0b0d11] border border-slate-700 px-3 py-2 text-sm">
                  <option value="all">All sessions</option><option value="live">Live now</option><option value="upcoming">Upcoming</option><option value="stream">Live streams</option><option value="meeting">Meetings</option><option value="recording">Recordings</option>
                </select>
                <select value={sessionSort} onChange={(e)=>setSessionSort(e.target.value)} className="rounded-md bg-[#0b0d11] border border-slate-700 px-3 py-2 text-sm">
                  <option value="soonest">Soonest first</option><option value="newest">Newest first</option><option value="host">Host A–Z</option>
                </select>
              </div>
            </div>
            <div className="rounded-2xl border border-slate-800 overflow-hidden bg-[#111318]">
              {loading ? (
                <div className="p-8 text-slate-500">Loading…</div>
              ) : sessions.length === 0 ? (
                <div className="p-10 text-center"><CalendarClock className="w-10 h-10 text-slate-600 mx-auto mb-3" /><div>No sessions available</div></div>
              ) : sessions.map((session) => (
                <div key={session.id} className="flex items-center gap-3 px-5 py-4 border-b last:border-b-0 border-slate-800 hover:bg-white/[.03]">
                  <Link to={canManageSession(session) ? `/communications/studio/${session.id}` : `/communications/rooms/${session.id}`} className="min-w-0 flex-1 flex items-center gap-4">
                    <Radio className="w-5 h-5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{session.title}</div>
                      <div className="text-xs text-slate-500">{session.status === "live" ? "LIVE NOW" : session.scheduled_at ? new Date(session.scheduled_at).toLocaleString() : "Ready"}{session.website_live_review_status === "pending" ? " · Nation Live review pending" : ""}</div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-500 flex-shrink-0" />
                  </Link>
                  {canManageSession(session) && (
                    <CommunicationsSessionActions
                      session={session}
                      destinations={destinations}
                      providers={providers}
                      maxDestinations={maxDestinations}
                      onChanged={load}
                    />
                  )}
                </div>
              ))}
            </div>
          </section>

          <ConnectedAccounts destinations={destinations} providers={providers} onRefresh={load} />
        </main>
      </div>

      {modal && (
        <CreateSessionModal
          mode={modal}
          onClose={() => setModal(null)}
          destinations={destinations}
          providers={providers}
          accessOptions={accessOptions}
          account={account}
          cloudRecordingEnabled={Boolean(storage?.cloud_recording_enabled)}
          maxDestinations={maxDestinations}
        />
      )}
    </div>
  );
}
